import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';
import { AlertTriangle, Trash2 } from 'lucide-react';

import {
  deleteMigration,
  deleteMigrationErrorCode,
  waveActiveDetail,
  type DeleteMigrationResult,
} from '../api/migrations';
import EnvironmentEcho from './EnvironmentEcho';
import { IDENTIFIER, NUMERIC, STROKE, TEXT } from '../theme/tokens';

// FD-478 (FD-ADR-044) — deleting a campaign.
//
// THE AFFORDANCE IS ON CAMPAIGN DETAIL, NOT ON THE LIST, and that is the first
// decision rather than a placement detail: a delete on a list row is one misclick
// away from the row above it, and the row above it is a different customer's
// campaign.
//
// The typed name gates this button, AND THAT IS NOT THE CONTROL. The server
// compares it too (FD-477) — a console-only check is the FD-296 class of gate,
// one that only runs where someone happens to be standing, and therefore no gate
// at all against a script, a stale bundle, or a second client. The field is here
// because it makes the act deliberate, not because it makes it safe.
//
// `409 WAVE_ACTIVE` RENDERS AS GUIDANCE, NOT AS AN ERROR. A campaign whose wave
// is still running is not a broken thing; it is a two-step operation with the
// first step outstanding. Amber is the console's "a human must act" colour and
// red is its "this is broken" colour (FLUENT_UI_ADDENDUM), so painting this red
// would tell an operator their campaign is in trouble when it is simply busy.
// The refusal names the next step and offers to take them to it.
//
// The only semantic red in here is the confirm button, deliberately NOT on the
// brand-token cascade — destructive actions stay red across any future brand
// recolour (the ConfirmRemoveDevicesModal precedent).

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', rowGap: '14px', minWidth: '480px' },
  destructive: {
    backgroundColor: '#b91c1c',
    color: '#ffffff',
    border: 'none',
    ':hover': { backgroundColor: '#991b1b', color: '#ffffff' },
    ':active': { backgroundColor: '#7f1d1d', color: '#ffffff' },
    ':disabled': { backgroundColor: '#fca5a5', color: '#ffffff' },
  },
});

/**
 * What a delete reaches, stated BEFORE it is authorised.
 *
 * The server returns its own `blastRadius` on the 202 and the receipt renders
 * that one — but the 202 arrives *after* the act, and the sentence that matters
 * is the one an operator reads before consenting. So this list exists, and the
 * receipt shows the server's so a divergence between the two is visible rather
 * than hidden behind whichever copy happened to be displayed.
 */
const BLAST_RADIUS = [
  'ATC MINT — every assigned device is deregistered from the cross-tenant boot index.',
  "ATC licensing — the wave's reservation is settled.",
  'The destination tenant — the wave’s package user is deleted, killing its device tokens.',
  'This deployment — the campaign, its devices, identity mappings, events, manifests and flight plans.',
];

export interface DeleteMigrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationId: string;
  migrationName: string;
  /**
   * Called once the delete has been accepted (202) and the operator closes the
   * dialog. The parent navigates away and invalidates its queries — the campaign
   * it is displaying is being removed.
   */
  onDeleted?: () => void;
  /**
   * Takes the operator to the wave lifecycle panel from the `WAVE_ACTIVE`
   * refusal. Optional: where the dialog is mounted somewhere the wave panel is
   * not reachable, the guidance still names the step, it just cannot offer the
   * jump. A dead button would be worse than none.
   */
  onGoToWave?: () => void;
}

export default function DeleteMigrationDialog({
  open,
  onOpenChange,
  migrationId,
  migrationName,
  onDeleted,
  onGoToWave,
}: DeleteMigrationDialogProps) {
  const styles = useStyles();
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DeleteMigrationResult | null>(null);
  const [waveActive, setWaveActive] = useState<{ bprtStatus?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A re-open starts fresh. Fluent unmounts DialogSurface on close, but this
  // component stays mounted, so without the reset a second open would still be
  // showing the first attempt's refusal.
  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setBusy(false);
      setResult(null);
      setWaveActive(null);
      setError(null);
    }
  }, [open]);

  // The name is compared ordinally and case-sensitively, exactly as the server
  // does — a button that enabled on a casing the server then refuses would train
  // an operator to distrust the field.
  const matches = migrationName.length > 0 && confirmText.trim() === migrationName;

  async function onConfirm() {
    if (!matches || busy) return;
    setBusy(true);
    setError(null);
    setWaveActive(null);
    try {
      setResult(await deleteMigration(migrationId, confirmText.trim()));
    } catch (err) {
      const code = deleteMigrationErrorCode(err);
      if (code === 'WAVE_ACTIVE') {
        setWaveActive(waveActiveDetail(err) ?? {});
      } else {
        setError(err instanceof Error ? err.message : 'The delete could not be started.');
      }
    } finally {
      setBusy(false);
    }
  }

  function close() {
    if (result) onDeleted?.();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {result ? 'Delete started' : `Delete ${migrationName}`}
          </DialogTitle>

          <DialogContent>
            <div className={styles.body}>
              {/* FD-422 / FD-ADR-040 §8 — the last thing read before the click,
                  and the only defence of the four in the operator's field of
                  view at that moment. */}
              <EnvironmentEcho label="This deletes from" />

              {result ? (
                <Receipt result={result} />
              ) : (
                <>
                  <MessageBar
                    intent="error"
                    icon={<AlertTriangle size={16} strokeWidth={1.5} />}
                  >
                    <MessageBarBody>
                      <MessageBarTitle>
                        This cannot be undone, and it reaches outside this deployment.
                      </MessageBarTitle>
                      <ul className="mt-1 list-disc pl-5 text-[13px]">
                        {BLAST_RADIUS.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </MessageBarBody>
                  </MessageBar>

                  {waveActive && (
                    // Guidance, not a failure. Amber is "a human must act".
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        <MessageBarTitle>Complete or abort this wave first</MessageBarTitle>
                        This campaign&rsquo;s wave credential is still active
                        {waveActive.bprtStatus ? ` (${waveActive.bprtStatus})` : ''}. Deleting
                        it would revoke a live tenant-join credential as a side effect, so that
                        is a separate, deliberate step. Nothing has been changed.
                      </MessageBarBody>
                      {onGoToWave && (
                        <MessageBarActions>
                          <Button
                            size="small"
                            onClick={() => {
                              onOpenChange(false);
                              onGoToWave();
                            }}
                          >
                            Go to the wave panel
                          </Button>
                        </MessageBarActions>
                      )}
                    </MessageBar>
                  )}

                  {error && (
                    <MessageBar intent="error">
                      <MessageBarBody>{error}</MessageBarBody>
                    </MessageBar>
                  )}

                  <Field
                    label={`Type ${migrationName} to confirm`}
                    hint="The server checks this too — the button is a convenience, not the control."
                  >
                    <Input
                      value={confirmText}
                      onChange={(_, data) => setConfirmText(data.value)}
                      placeholder={migrationName}
                      disabled={busy}
                    />
                  </Field>
                </>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={close} disabled={busy}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                appearance="primary"
                className={styles.destructive}
                icon={<Trash2 size={16} strokeWidth={1.5} />}
                disabled={!matches || busy}
                onClick={() => void onConfirm()}
              >
                {busy ? 'Starting…' : 'Delete campaign'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * The 202 receipt.
 *
 * It says STARTED, never "deleted", because the endpoint queues the teardown and
 * returns immediately (FD-ADR-044 D6) — a 200-device campaign takes minutes of
 * sequential MINT deregistration. Saying "deleted" and then showing the campaign
 * still listed for a few seconds is how a console teaches an operator to refresh
 * and wonder.
 */
function Receipt({ result }: { result: DeleteMigrationResult }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px]" style={{ color: TEXT.secondary }}>
        The teardown is running. <strong style={{ color: TEXT.primary }}>{result.migrationName}</strong>{' '}
        and everything it owns will be removed; the record of this delete stays under the
        run id below.
      </p>
      <div
        className="flex flex-col gap-1 pt-3"
        style={{ borderTop: `1px solid ${STROKE.subtle}` }}
      >
        <span className="text-[11px] uppercase tracking-wide" style={{ color: TEXT.secondary }}>
          Run id
        </span>
        <span style={{ ...IDENTIFIER, ...NUMERIC, color: TEXT.primary }}>{result.runId}</span>
      </div>
      {result.blastRadius?.length > 0 && (
        // The SERVER's list, not the one shown before confirming — so the two can
        // be compared if they ever drift apart.
        <ul className="list-disc pl-5 text-[13px]" style={{ color: TEXT.secondary }}>
          {result.blastRadius.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
